import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { VerifyEmailResponse } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { ButtonLink, FormNotice, LoadingButton } from "../components/form";

type Phase = "checking" | "verified" | "failed" | "waiting";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh } = useSession();

  const [phase, setPhase] = useState<Phase>(token ? "checking" : "waiting");
  const [error, setError] = useState<string | undefined>();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase("waiting");
      return;
    }
    let cancelled = false;
    setPhase("checking");
    api<VerifyEmailResponse>("POST", "/auth/verify-email", { token })
      .then(async () => {
        if (cancelled) return;
        await refresh();
        setPhase("verified");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiFail ? err.message : "Couldn't verify that link.");
        setPhase("failed");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (phase === "checking") {
    return (
      <AuthShell title="Verifying your email…" subtitle="This only takes a moment.">
        <div className="status-row">Checking your link…</div>
      </AuthShell>
    );
  }

  if (phase === "verified") {
    return (
      <AuthShell title="Email verified" subtitle="You're all set — your address is confirmed.">
        <ButtonLink to="/orgs" block>
          Continue
        </ButtonLink>
      </AuthShell>
    );
  }

  if (phase === "failed") {
    return (
      <AuthShell title="That link didn't work" subtitle="It may have expired or already been used.">
        <FormNotice tone="error">{error}</FormNotice>
        <LoadingButton block onClick={() => window.location.assign("/verify")}>
          Try again
        </LoadingButton>
      </AuthShell>
    );
  }

  async function onResend() {
    setResending(true);
    setResent(false);
    try {
      await api("POST", "/auth/resend-verification");
      setResent(true);
    } catch {
      // Best-effort — matches the endpoint's own always-204 contract
    } finally {
      setResending(false);
    }
  }

  // "waiting" — landed here right after signup, no token in the URL yet.
  const address = user?.email ?? "your email address";
  return (
    <AuthShell
      title="Check your email"
      subtitle={`We sent a verification link to ${address}. It expires in 24 hours.`}
    >
      <FormNotice icon="envelope-simple">
        You can keep recording while you wait — verification is only needed before you publish
        or share.
      </FormNotice>
      <div className="auth-actions">
        {resent ? (
          <span className="gk-sm" role="status">Verification email sent — check your inbox.</span>
        ) : (
          <LoadingButton
            variant="secondary"
            type="button"
            onClick={onResend}
            loading={resending}
            disabled={resending}
          >
            Resend email
          </LoadingButton>
        )}
        <ButtonLink to="/signup" variant="ghost">
          Change address
        </ButtonLink>
      </div>
    </AuthShell>
  );
}
